import React, { useState } from "react";
import { Wallet as WalletIcon, Gift, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { inrRaw } from "../lib/currency";
import { toast } from "sonner";

const PLANS = [
  { id: "starter", label: "Starter", credits: 2000, price: 1600, perk: "5% back on every order" },
  { id: "family",  label: "Family",  credits: 5000, price: 3800, perk: "10% back + free delivery" },
  { id: "pantry",  label: "Pantry",  credits: 11000, price: 7500, perk: "15% back + priority batches" },
];

export const Wallet = () => {
  const [balance, setBalance] = useState(0);
  const [topup, setTopup] = useState("");

  const addPlan = (plan) => {
    setBalance((b) => b + plan.credits);
    toast.success(`${plan.label} pack loaded`, {
      description: `+${inrRaw(plan.credits)} credits added to your wallet`,
    });
  };

  const addTopup = () => {
    const amt = parseFloat(topup);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setBalance((b) => b + amt);
    setTopup("");
    toast.success(`${inrRaw(amt)} added to wallet`);
  };

  return (
    <section id="wallet" data-testid="wallet-section" className="py-20 md:py-28 bg-background">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-5 gap-10 items-start">
        <div className="md:col-span-2">
          <div className="font-mono-ui text-xs uppercase tracking-widest text-accent mb-3">
            — 04 / Pantry Wallet
          </div>
          <h2 className="font-display text-4xl md:text-6xl font-semibold leading-tight tracking-tight">
            Load once.<br />
            <em className="italic text-accent font-medium">Save</em> forever.
          </h2>
          <p className="mt-6 text-muted-foreground max-w-md leading-relaxed">
            Top up your Pantry Wallet, get bonus credits on every load. Use it
            for oils, ghee, makhan, vegetables and laddu — anything on the shelf.
          </p>

          <div
            data-testid="wallet-balance-card"
            className="mt-8 relative overflow-hidden rounded-2xl bg-primary text-primary-foreground p-6 shadow-xl"
          >
            <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-accent/20 blur-2xl" />
            <div className="flex items-center gap-3">
              <WalletIcon className="w-5 h-5 text-accent" />
              <div className="font-mono-ui text-[10px] uppercase tracking-widest text-primary-foreground/70">
                Your balance
              </div>
            </div>
            <div className="mt-4 font-display text-6xl font-semibold">
              {inrRaw(balance)}
            </div>
            <div className="mt-6 flex gap-2">
              <Input
                data-testid="wallet-topup-input"
                type="number"
                min="1"
                step="1"
                placeholder="Add custom ₹ amount"
                value={topup}
                onChange={(e) => setTopup(e.target.value)}
                className="h-11 bg-background text-foreground border-0"
              />
              <Button
                data-testid="wallet-topup-btn"
                onClick={addTopup}
                className="h-11 rounded-md bg-accent text-accent-foreground hover:bg-accent/90 shrink-0"
              >
                Load
              </Button>
            </div>
          </div>
        </div>

        <div className="md:col-span-3 grid sm:grid-cols-3 gap-4">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              data-testid={`wallet-plan-${plan.id}`}
              className="relative rounded-2xl border border-border bg-card p-6 flex flex-col hover-lift"
            >
              {plan.id === "family" && (
                <span className="absolute -top-3 left-6 px-2.5 py-1 bg-accent text-accent-foreground text-[10px] font-mono-ui uppercase tracking-widest rounded-full">
                  <Sparkles className="w-3 h-3 inline mr-1 -mt-0.5" />
                  Most loved
                </span>
              )}
              <div className="font-mono-ui text-xs uppercase tracking-widest text-muted-foreground">
                {plan.label}
              </div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-display text-5xl font-semibold">{inrRaw(plan.credits)}</span>
                <span className="text-sm text-muted-foreground">credits</span>
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                Pay {inrRaw(plan.price)} · save {inrRaw(plan.credits - plan.price)}
              </div>
              <div className="mt-4 flex items-start gap-2 text-sm">
                <Gift className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                <span>{plan.perk}</span>
              </div>
              <Button
                data-testid={`wallet-buy-${plan.id}`}
                onClick={() => addPlan(plan)}
                className="mt-6 h-11 w-full rounded-full bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground text-sm font-semibold"
              >
                Load {inrRaw(plan.credits)}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
